import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  Plus, Trash2, Download, Upload, Lock, ShieldQuestion, ChefHat, Utensils,
  X, ArrowRight, Info,
} from "lucide-react";
import StepLayout from "./StepLayout";

/**
 * Step 9 — Food Prep setup.
 *
 * Three cards, top-down:
 *   1. Food categories    — chip list; defaults are locked (Vanguard Beacon
 *                           platform defaults) and can only be edited/removed
 *                           by super_admin. Tenant may add / delete own.
 *   2. Food catalog       — preset list with an Import Excel / Export Excel
 *                           pair (same endpoint & template as the live app
 *                           at /app/food-prep) plus a small "Add preset"
 *                           form for quick single entries.
 *   3. Dispose confirm    — checkbox + value/unit input. If enabled, chefs
 *      guard                are asked to confirm before disposing items
 *                           that still have more shelf life than this
 *                           minimum. Stored on tenant.food_prep_settings.
 *
 * All three sections are optional — clicking Save & continue always advances,
 * whether or not the owner has actually configured anything. Everything is
 * fully editable later from the main Food Prep → Catalog & Settings tab.
 */
export default function Step9FoodPrep({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify, confirm } = useDialog();
  const [categories, setCategories] = useState([]);
  const [presets, setPresets] = useState([]);
  const [settings, setSettings] = useState({
    retention_months: 12,
    default_amber_window_minutes: 120,
    dispose_confirm_min_shelf_life_minutes: 0,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);
  const [showAddPreset, setShowAddPreset] = useState(false);
  const fileRef = useRef(null);

  // Dispose-confirmation guard derived state — computed live from `settings`.
  const guardInit = useMemo(() => {
    const mins = settings.dispose_confirm_min_shelf_life_minutes || 0;
    if (mins > 0) {
      return mins % 1440 === 0
        ? { enabled: true, value: mins / 1440, unit: "days" }
        : { enabled: true, value: Math.max(1, Math.round(mins / 60)), unit: "hours" };
    }
    return { enabled: false, value: 1, unit: "days" };
  }, [settings.dispose_confirm_min_shelf_life_minutes]);

  const [guardEnabled, setGuardEnabled] = useState(false);
  const [guardValue, setGuardValue] = useState(1);
  const [guardUnit, setGuardUnit] = useState("days");
  useEffect(() => {
    setGuardEnabled(guardInit.enabled);
    setGuardValue(guardInit.value);
    setGuardUnit(guardInit.unit);
  }, [guardInit]);

  const load = async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get("/food-prep/categories"),
        api.get("/food-prep/presets"),
        api.get("/food-prep/settings"),
      ]);
      setCategories(c.data.categories || []);
      setPresets(p.data.presets || []);
      setSettings(s.data);
    } catch (e) { console.debug("step9 load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const [newCatName, setNewCatName] = useState("");
  const addCategory = async (e) => {
    e?.preventDefault();
    const name = newCatName.trim();
    if (!name) return;
    try {
      await api.post("/food-prep/categories", { name });
      setNewCatName("");
      await load();
    } catch (ex) {
      await notify({ title: "Couldn't add category", message: formatErr(ex.response?.data?.detail), variant: "err" });
    }
  };
  const removeCategory = async (c) => {
    if (!(await confirm({
      title: "Delete category?",
      message: `Remove "${c.name}"? Existing catalog items keep their label.`,
      danger: true, confirmLabel: "Delete",
    }))) return;
    try { await api.delete(`/food-prep/categories/${c.id}`); await load(); }
    catch (ex) {
      await notify({ title: "Delete failed", message: formatErr(ex.response?.data?.detail), variant: "err" });
    }
  };

  const removePreset = async (p) => {
    if (!(await confirm({
      title: "Delete preset?",
      message: `Remove "${p.name}" from the catalog?`,
      danger: true, confirmLabel: "Delete",
    }))) return;
    try { await api.delete(`/food-prep/presets/${p.id}`); await load(); }
    catch (ex) {
      await notify({ title: "Delete failed", message: formatErr(ex.response?.data?.detail), variant: "err" });
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/food-prep/presets/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "food_catalog.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      await notify({ title: "Export failed", message: formatErr(ex.response?.data?.detail), variant: "err" });
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/food-prep/presets/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const lines = [`Created ${data.created || 0} preset(s).`];
      if (data.categories_created?.length) lines.push(`New categories: ${data.categories_created.join(", ")}.`);
      if (data.duplicates?.length) lines.push(`Skipped duplicates: ${data.duplicates.length}.`);
      if (data.errors?.length) lines.push(`Errors: ${data.errors.length}.`);
      await notify({ title: "Import complete", message: lines.join(" "), variant: "ok" });
      await load();
    } catch (ex) {
      await notify({ title: "Import failed", message: formatErr(ex.response?.data?.detail), variant: "err" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveAndContinue = async () => {
    setBusy(true); setErr("");
    try {
      const gMins = guardEnabled
        ? Math.round(Number(guardValue) * (guardUnit === "days" ? 1440 : 60))
        : 0;
      if (guardEnabled && (!gMins || gMins < 1)) {
        setErr("Enter a minimum shelf life of at least 1.");
        setBusy(false);
        return;
      }
      await api.put("/food-prep/settings", { dispose_confirm_min_shelf_life_minutes: gMins });
      await onAdvance({ food_prep: true });
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Couldn't save the guard setting. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Set up your Food Prep catalog"
      subtitle="Categories, presets, and the dispose-confirmation safety net."
      description={
        <div className="space-y-3">
          <p>Chefs label prepared food with a use-by date and a QR code. A well-populated catalog turns each label into two taps.</p>
          <p className="text-xs opacity-70">Everything here can be edited later from <strong>Food Prep → Catalog</strong>. Skip anything you're not sure about.</p>
        </div>
      }
      onBack={onBack}
      onNext={saveAndContinue}
      onFinishLater={onFinishLater}
      busy={busy}
      errorMsg={err}
      nextLabel="Save & continue"
    >
      <div className="space-y-5">
        {/* -------- 1. Categories -------- */}
        <div className="vb-card p-5" data-testid="wiz-fp-categories">
          <div className="flex items-center gap-2 mb-1">
            <Utensils size={16} />
            <h3 className="vb-heading text-lg m-0">Food categories</h3>
          </div>
          <p className="text-xs opacity-60 mb-3">
            Group your catalog items (e.g. "Sauces", "Cooked Rice"). Categories tagged
            <span className="inline-flex items-center gap-1 mx-1 vb-pill text-[10px]"><Lock size={9} /> Default</span>
            come with Vanguard Beacon and apply to every customer. Add or remove your own below.
          </p>
          <form onSubmit={addCategory} className="flex gap-2 mb-3">
            <input
              className="vb-input"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="e.g. Sauces & Marinades"
              maxLength={60}
              data-testid="wiz-fp-cat-input"
            />
            <button type="submit" className="vb-btn sm shrink-0" data-testid="wiz-fp-cat-add">
              <Plus size={14} /> Add
            </button>
          </form>
          {categories.length === 0 ? (
            <div className="text-sm opacity-60">No categories yet.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="wiz-fp-cat-list">
              {categories.map((c) => (
                <span
                  key={c.id}
                  className="vb-pill inline-flex items-center gap-1"
                  style={{ paddingRight: c.is_default ? undefined : 6 }}
                  title={c.is_default ? "Default category set by Vanguard Beacon" : undefined}
                  data-testid={`wiz-fp-cat-pill-${c.id}`}
                >
                  {c.is_default && <Lock size={9} className="opacity-60" />}
                  {c.name}
                  {!c.is_default && (
                    <button
                      type="button"
                      className="ml-1 opacity-60 hover:opacity-100"
                      onClick={() => removeCategory(c)}
                      aria-label={`Delete ${c.name}`}
                      data-testid={`wiz-fp-cat-del-${c.id}`}
                    >
                      <X size={11} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* -------- 2. Food catalog (presets) -------- */}
        <div className="vb-card p-5" data-testid="wiz-fp-catalog">
          <div className="flex items-center gap-2 mb-1 flex-wrap justify-between">
            <div className="flex items-center gap-2">
              <ChefHat size={16} />
              <h3 className="vb-heading text-lg m-0">Food catalog ({presets.length})</h3>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                onChange={onImportFile} data-testid="wiz-fp-import-input" />
              <button type="button" className="vb-btn ghost sm"
                onClick={() => fileRef.current?.click()} disabled={importing}
                data-testid="wiz-fp-import">
                <Upload size={14} /> {importing ? "Importing…" : "Import Excel"}
              </button>
              <button type="button" className="vb-btn ghost sm" onClick={exportXlsx}
                data-testid="wiz-fp-export">
                <Download size={14} /> Export template
              </button>
              <button type="button" className="vb-btn sm" onClick={() => setShowAddPreset(true)}
                data-testid="wiz-fp-preset-add">
                <Plus size={14} /> Add preset
              </button>
            </div>
          </div>
          <p className="text-xs opacity-60 mb-3">
            <strong>Export</strong> to get a pre-filled Excel template (Food Catalog + Categories tabs,
            with drop-downs bound to your live categories). Add rows, then <strong>Import</strong> to bulk-load.
            Duplicates and invalid rows are reported and skipped.
          </p>
          {presets.length === 0 ? (
            <div className="text-sm opacity-60">No catalog items yet — add a few via the buttons above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="vb-table" data-testid="wiz-fp-preset-table">
                <thead>
                  <tr><th>Name</th><th>Category</th><th style={{ whiteSpace: "nowrap" }}>Shelf life</th><th style={{ width: 40 }}></th></tr>
                </thead>
                <tbody>
                  {presets.map((p) => {
                    const mins = p.default_shelf_life_minutes || 0;
                    const shelf = mins % 1440 === 0
                      ? `${mins / 1440} day${mins / 1440 === 1 ? "" : "s"}`
                      : `${Math.round(mins / 60)} hr${Math.round(mins / 60) === 1 ? "" : "s"}`;
                    return (
                      <tr key={p.id} data-testid={`wiz-fp-preset-row-${p.id}`}>
                        <td><strong>{p.name}</strong></td>
                        <td>{p.category || <span className="opacity-40">—</span>}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{shelf}</td>
                        <td>
                          <button type="button" className="vb-btn ghost sm"
                            style={{ color: "var(--vb-error)" }}
                            onClick={() => removePreset(p)}
                            data-testid={`wiz-fp-preset-del-${p.id}`}
                            aria-label={`Delete ${p.name}`}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* -------- 3. Dispose confirmation guard -------- */}
        <div className="vb-card p-5" data-testid="wiz-fp-guard">
          <div className="flex items-center gap-2 mb-1">
            <ShieldQuestion size={16} />
            <h3 className="vb-heading text-lg m-0">Dispose confirmation guard</h3>
          </div>
          <p className="text-xs opacity-60 mb-3">
            In the Scanner's Active Tracking mode, scanned items that still have <em>more</em>
            shelf life than the minimum below will ask for a "Are you sure?" confirmation
            before being disposed — protecting food that's still good.
          </p>
          <label className="flex items-center gap-2 mb-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={guardEnabled}
              onChange={(e) => setGuardEnabled(e.target.checked)}
              data-testid="wiz-fp-guard-enabled"
            />
            <span>Require confirmation above a minimum shelf life</span>
          </label>
          {guardEnabled && (
            <div className="flex gap-2 items-center" data-testid="wiz-fp-guard-input">
              <input
                className="vb-input"
                type="number"
                min="1"
                value={guardValue}
                onChange={(e) => setGuardValue(e.target.value)}
                style={{ maxWidth: 110 }}
                data-testid="wiz-fp-guard-value"
              />
              <select
                className="vb-input"
                value={guardUnit}
                onChange={(e) => setGuardUnit(e.target.value)}
                style={{ maxWidth: 110 }}
                data-testid="wiz-fp-guard-unit"
              >
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
              <span className="text-xs opacity-60 flex items-center gap-1">
                <Info size={11} /> Anything with more shelf life will prompt to confirm.
              </span>
            </div>
          )}
        </div>
      </div>

      {showAddPreset && (
        <AddPresetModal
          categories={categories}
          onClose={() => setShowAddPreset(false)}
          onSaved={async () => { setShowAddPreset(false); await load(); }}
        />
      )}
    </StepLayout>
  );
}

/**
 * Lightweight inline "Add preset" modal — mirrors the fields the main
 * FoodPrep PresetModal uses, but stripped down. Advanced settings (amber
 * window override, edits) remain available on the main Food Prep page.
 */
function AddPresetModal({ categories, onClose, onSaved }) {
  const { notify } = useDialog();
  const [name, setName] = useState("");
  const [value, setValue] = useState(1);
  const [unit, setUnit] = useState("days");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const nm = name.trim();
    if (!nm) return;
    setBusy(true);
    try {
      const mins = Math.round(Number(value) * (unit === "days" ? 1440 : 60));
      await api.post("/food-prep/presets", {
        name: nm,
        default_shelf_life_minutes: mins,
        amber_window_minutes: null,
        category: category || null,
      });
      onSaved();
    } catch (ex) {
      await notify({ title: "Couldn't add preset", message: formatErr(ex.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="wiz-fp-preset-modal-bg">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }} data-testid="wiz-fp-preset-modal">
        <div className="flex items-start justify-between mb-3">
          <h2 className="vb-heading text-xl m-0">Add catalog item</h2>
          <button type="button" className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs opacity-70">Name *</label>
            <input className="vb-input" required maxLength={120} value={name}
              onChange={(e) => setName(e.target.value)} placeholder="e.g. Bolognese sauce"
              data-testid="wiz-fp-preset-name" />
          </div>
          <div>
            <label className="text-xs opacity-70">Category</label>
            <select className="vb-input" value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="wiz-fp-preset-cat">
              <option value="">— None —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs opacity-70">Default shelf life *</label>
            <div className="flex gap-2">
              <input className="vb-input" type="number" min="1" required value={value}
                onChange={(e) => setValue(e.target.value)} style={{ maxWidth: 120 }}
                data-testid="wiz-fp-preset-val" />
              <select className="vb-input" value={unit} onChange={(e) => setUnit(e.target.value)}
                style={{ maxWidth: 120 }} data-testid="wiz-fp-preset-unit">
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="wiz-fp-preset-submit">
              {busy ? "Adding…" : <>Add <ArrowRight size={13} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
